import React from "react";
import { BlockMath, ChoiceLabel, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";
import CuboidBoxDiagram from "../../../../components/diagrams/CuboidBoxDiagram";

const LETTERS = ["A", "B", "C", "D"];

function shuffle(values) {
  const result = [...values];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function buildOptions(correct, distractors, keepOrder = false) {
  const values = [correct, ...distractors.filter((value) => value !== correct)];
  const unique = [...new Set(values)].slice(0, 4);
  const ordered = (keepOrder ? unique : shuffle(unique)).map((value) => ({
    value,
    label: String(value),
  }));
  return {
    options: ordered,
    correctIndex: ordered.findIndex((option) => option.value === correct),
  };
}

function buildData({ a, b, c, options = null }) {
  const surfaceArea = 2 * (a * b + a * c + b * c);
  const builtOptions =
    options ||
    buildOptions(surfaceArea, [
      a * b + a * c + b * c,
      2 * (a + b + c),
      2 * a * b + a * c + b * c,
    ]);
  return {
    a,
    b,
    c,
    surfaceArea,
    options: builtOptions.options,
    correctIndex: builtOptions.correctIndex,
  };
}

function createOriginalTask() {
  return {
    type: "E8CuboidSurfaceAreaClosedTask",
    data: buildData({
      a: 5,
      b: 6,
      c: 7,
      options: {
        options: [107, 172, 210, 214].map((value) => ({
          value,
          label: String(value),
        })),
        correctIndex: 3,
      },
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2025_MAJ",
      taskNumber: 15,
    },
    tags: ["e8", "stereometria", "prostopadloscian", "pole-powierzchni"],
  };
}

function randomInt(min, max) {
  return min + Math.floor(Math.random() * (max - min + 1));
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  let a;
  let b;
  let c;
  do {
    a = randomInt(3, 8);
    b = randomInt(4, 9);
    c = randomInt(5, 10);
  } while (new Set([a, b, c]).size < 3);

  return {
    type: "E8CuboidSurfaceAreaClosedTask",
    data: buildData({ a, b, c }),
    source: { variant: "random", basedOn: "E8_2025_MAJ_15" },
    tags: ["e8", "stereometria", "prostopadloscian", "pole-powierzchni"],
  };
}

function CuboidDiagram({ data }) {
  const [shortEdge, midEdge, longEdge] = [data.a, data.b, data.c].sort((x, y) => x - y);
  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "18px 0" }}>
      <CuboidBoxDiagram
        widthValue={shortEdge}
        depthValue={midEdge}
        heightValue={longEdge}
        frontLabel={String(shortEdge)}
        depthLabel={String(midEdge)}
        heightLabel={String(longEdge)}
      />
    </div>
  );
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Długości trzech krawędzi prostopadłościanu wychodzących z jednego
        wierzchołka są równe <InlineMath math={`${data.a}`} />,{" "}
        <InlineMath math={`${data.b}`} /> i <InlineMath math={`${data.c}`} />.
      </p>
      <CuboidDiagram data={data} />
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>Pole powierzchni całkowitej tego prostopadłościanu jest równe</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.label}>
            <ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        label: LETTERS[index],
        textNode: LETTERS[index],
      }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${data.surfaceArea}`} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}`} />
    </span>
  );
}

export function renderExplanation({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Prostopadłościan ma trzy pary jednakowych ścian.
        Pola tych różnych ścian to:
      </p>
      <BlockMath math={`${data.a}\\cdot ${data.b}=${data.a * data.b},\\quad ${data.a}\\cdot ${data.c}=${data.a * data.c},\\quad ${data.b}\\cdot ${data.c}=${data.b * data.c}`} />
      <p>
        <strong>Krok 2.</strong> Każda z tych ścian występuje dwa razy, więc:
      </p>
      <BlockMath math={`P_c=2\\left(${data.a * data.b}+${data.a * data.c}+${data.b * data.c}\\right)`} />
      <BlockMath math={`P_c=2\\cdot ${data.a * data.b + data.a * data.c + data.b * data.c}=${data.surfaceArea}`} />
      <p>
        Poprawna odpowiedź to <ChoiceLabel letter={LETTERS[data.correctIndex]} />
      </p>
    </div>
  );
}
